import { useEffect, useRef } from 'react';

/**
 * Full-screen falling golden sand. New grains spawn only while `emitting`;
 * when the user leaves the Sand tab the stream stops but grains already in
 * the air keep falling until they leave the screen. Mounted at app level so
 * the drain survives tab switches.
 */
export function SandBackground({ emitting }: { emitting: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const emitRef = useRef(emitting);
  emitRef.current = emitting;

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    let W = 0;
    let H = 0;
    interface Grain {
      x: number;
      y: number;
      s: number;
      v: number;
      a: number;
    }
    const grains: Grain[] = [];
    const resize = () => {
      W = c.width = window.innerWidth;
      H = c.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);
    const spawn = (n: number) => {
      for (let i = 0; i < n; i++) {
        grains.push({
          x: Math.random() * W,
          y: -4 - Math.random() * 20,
          s: 0.4 + Math.random() * 0.9,
          v: 1 + Math.random() * 2.4,
          a: 0.45 + Math.random() * 0.55,
        });
      }
    };
    if (emitRef.current) spawn(600);
    let raf = 0;
    const loop = () => {
      ctx.clearRect(0, 0, W, H);
      const cap = Math.min(1400, W * 1.2);
      if (emitRef.current && grains.length < cap) spawn(14);
      for (let i = grains.length - 1; i >= 0; i--) {
        const g = grains[i];
        g.y += g.v;
        g.x += Math.sin(g.y / 60) * 0.2;
        if (g.y > H) {
          if (emitRef.current) {
            g.y = -4;
            g.x = Math.random() * W;
          } else {
            grains.splice(i, 1);
            continue;
          }
        }
        ctx.globalAlpha = g.a;
        ctx.fillStyle = '#ffe9a8';
        ctx.fillRect(g.x, g.y, g.s, g.s);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="sandfx" aria-hidden="true" />;
}
